import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Phone, Menu, X, Globe } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../context/LanguageContext';

const langs = [
  { code: 'de', label: 'DE', flag: '🇩🇪' },
  { code: 'en', label: 'EN', flag: '🇬🇧' },
  { code: 'tr', label: 'TR', flag: '🇹🇷' },
];

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const location = useLocation();
  const { language, setLanguage, t } = useLanguage();

  const navLinks = [
    { label: t.nav.home, path: '/' },
    { label: t.nav.umzug, path: '/umzug-berlin' },
    { label: t.nav.montage, path: '/moebelmontage-berlin' },
    { label: t.nav.entruempelung, path: '/entruempelung-berlin' },
    { label: t.nav.kontakt, path: '/kontakt' },
  ];

  const currentLang = langs.find(l => l.code === language);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setLangOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/95 backdrop-blur-md shadow-lg shadow-black/50' : 'bg-black/70 backdrop-blur-sm'
      }`}
      data-testid="main-header"
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between py-3">
          <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="header-logo-link">
            <img
              src="https://customer-assets.emergentagent.com/job_d4e1a410-67d7-4e44-b13c-32da4b8a1883/artifacts/my73mjfj_WhatsApp%20Image%202025-12-21%20at%2020.34.25.jpeg"
              alt="Panthera Umzug Berlin"
              className="h-12 md:h-14 w-auto"
              loading="eager"
              width="56"
              height="56"
              fetchPriority="high"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  location.pathname === link.path
                    ? 'text-[#D4AF37] bg-[#D4AF37]/10'
                    : 'text-gray-300 hover:text-[#D4AF37] hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            {/* Language Switcher */}
            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm text-gray-300 hover:text-[#D4AF37] hover:bg-white/5 transition-colors"
                data-testid="language-switcher"
              >
                <Globe className="w-4 h-4" />
                <span className="font-medium">{currentLang?.label}</span>
              </button>
              {langOpen && (
                <div className="absolute right-0 top-full mt-1 bg-[#111] border border-[#2a2a2a] rounded-lg shadow-xl overflow-hidden min-w-[100px]" data-testid="language-dropdown">
                  {langs.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => { setLanguage(l.code); setLangOpen(false); }}
                      className={`w-full flex items-center gap-2 px-4 py-2.5 text-sm transition-colors ${
                        language === l.code ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-gray-300 hover:text-white hover:bg-white/5'
                      }`}
                      data-testid={`lang-${l.code}`}
                    >
                      <span>{l.flag}</span>
                      <span className="font-medium">{l.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a href="tel:+491631945793" className="hidden md:flex items-center gap-2 text-sm text-gray-300 hover:text-[#D4AF37] transition-colors" data-testid="header-phone">
              <Phone className="w-4 h-4" />
              <span>+49 163 1945793</span>
            </a>
            <Link to="/kontakt" className="hidden sm:block">
              <Button size="sm" data-testid="header-cta-btn" className="bg-gradient-to-r from-[#F1D27A] to-[#D4AF37] text-black font-bold px-4 py-2 text-sm rounded-lg hover:from-[#D4AF37] hover:to-[#8F6B1F] transition-all hover:scale-105">
                {t.header.cta}
              </Button>
            </Link>

            {/* Mobile Toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 text-gray-300 hover:text-[#D4AF37] transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Menü"
            >
              {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <div className="lg:hidden bg-black/95 backdrop-blur-md border-t border-[#2a2a2a]" data-testid="mobile-nav">
          <nav className="container mx-auto px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'text-[#D4AF37] bg-[#D4AF37]/10'
                    : 'text-gray-300 hover:text-[#D4AF37] hover:bg-white/5'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="flex items-center gap-2 px-4 py-2 mt-2">
              {langs.map((l) => (
                <button
                  key={l.code}
                  onClick={() => { setLanguage(l.code); }}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    language === l.code ? 'text-[#D4AF37] bg-[#D4AF37]/10' : 'text-gray-400 hover:text-white'
                  }`}
                >
                  {l.flag} {l.label}
                </button>
              ))}
            </div>
            <a href="tel:+491631945793" className="flex items-center gap-2 px-4 py-3 text-[#D4AF37] font-medium">
              <Phone className="w-4 h-4" /> +49 163 1945793
            </a>
          </nav>
        </div>
      )}
    </header>
  );
};
